// Reading, and removing, the textures of a GLB without touching its geometry.
//
// A phone does not run out of room because a file is large, but because every
// texture in it is uncompressed to RGBA on the GPU: a 4096×4096 JPEG of 1 MB
// becomes 64 MB of video memory, and a house model full of them lands well
// past what iOS gives a tab before it kills the page.

const GLB_MAGIC = 0x46546c67; // 'glTF', little-endian
const JSON_CHUNK = 0x4e4f534a; // 'JSON'
const SPACE = 0x20; // what the spec pads a JSON chunk with

export interface GlbTextureInfo {
  /** Number of images embedded in the file. */
  images: number;
  /** Their compressed size — what they cost on disk, not in memory. */
  imageBytes: number;
}

interface JsonChunk {
  /** Byte offset of the JSON chunk's data inside the buffer. */
  offset: number;
  /** Its length in bytes, which a rewrite may not exceed. */
  length: number;
  gltf: GltfRoot;
}

interface GltfRoot {
  images?: unknown[];
  textures?: unknown[];
  samplers?: unknown[];
  bufferViews?: { byteLength?: number }[];
  materials?: Record<string, unknown>[];
  extensionsUsed?: string[];
  [key: string]: unknown;
}

/** Reads the JSON chunk of a GLB. Returns null for anything that is not one. */
function readJsonChunk(buffer: ArrayBuffer): JsonChunk | null {
  if (buffer.byteLength < 20) return null;
  const view = new DataView(buffer);
  if (view.getUint32(0, true) !== GLB_MAGIC) return null;
  if (view.getUint32(12 + 4, true) !== JSON_CHUNK) return null;

  const length = view.getUint32(12, true);
  const offset = 20;
  if (offset + length > buffer.byteLength) return null;

  try {
    const text = new TextDecoder().decode(new Uint8Array(buffer, offset, length));
    const gltf = JSON.parse(text) as GltfRoot;
    return { offset, length, gltf };
  } catch {
    return null;
  }
}

/**
 * What a GLB carries in textures, read from its JSON chunk alone. The caller
 * can hand this to the user before committing to a parse that may not survive.
 */
export function readGlbTextureInfo(buffer: ArrayBuffer): GlbTextureInfo | null {
  const chunk = readJsonChunk(buffer);
  if (!chunk) return null;

  const images = chunk.gltf.images ?? [];
  if (images.length === 0) return { images: 0, imageBytes: 0 };

  const views = chunk.gltf.bufferViews ?? [];
  let imageBytes = 0;
  for (const image of images as { bufferView?: number }[]) {
    if (image.bufferView == null) continue;
    imageBytes += views[image.bufferView]?.byteLength ?? 0;
  }
  return { images: images.length, imageBytes };
}

/** Every place a glTF material can point at a texture. */
function stripMaterial(material: Record<string, unknown>): void {
  const pbr = material.pbrMetallicRoughness as Record<string, unknown> | undefined;
  if (pbr) {
    delete pbr.baseColorTexture;
    delete pbr.metallicRoughnessTexture;
  }
  delete material.normalTexture;
  delete material.occlusionTexture;
  delete material.emissiveTexture;
  // Material extensions carry their own texture references (spec-gloss keeps
  // diffuseTexture there), and none of them survive without the texture array.
  delete material.extensions;
}

/**
 * Removes every texture from a GLB **in place** and reports whether it did.
 *
 * The rewritten JSON is always shorter than the original, so it is padded back
 * to the same length with spaces: the BIN chunk then stays exactly where it
 * was and the 190 MB of geometry never has to be copied. The image bytes stay
 * in the buffer as bufferViews nobody reads any more — dead weight on disk,
 * but nothing decodes them, which is the whole point.
 *
 * Returns false when the buffer is not a GLB, has no textures, or the rewrite
 * would not fit, in which case the buffer is left untouched.
 */
export function stripGlbTextures(buffer: ArrayBuffer): boolean {
  const chunk = readJsonChunk(buffer);
  if (!chunk) return false;

  const { gltf } = chunk;
  if (!gltf.images?.length && !gltf.textures?.length) return false;

  delete gltf.images;
  delete gltf.textures;
  delete gltf.samplers;
  for (const material of gltf.materials ?? []) stripMaterial(material);

  const encoded = new TextEncoder().encode(JSON.stringify(gltf));
  // Dropping entries can only shrink the JSON, but never write past the chunk.
  if (encoded.length > chunk.length) return false;

  const target = new Uint8Array(buffer, chunk.offset, chunk.length);
  target.set(encoded);
  target.fill(SPACE, encoded.length);
  return true;
}

/**
 * The same answer straight from a file on disk, reading only the header and
 * the JSON chunk. A 185 MB model is peeked at in a few hundred KB, so the
 * question of what to do about it can be asked before anything is committed.
 */
export async function peekGlbTextures(file: Blob): Promise<GlbTextureInfo | null> {
  try {
    const head = await file.slice(0, 20).arrayBuffer();
    if (head.byteLength < 20) return null;
    const view = new DataView(head);
    if (view.getUint32(0, true) !== GLB_MAGIC) return null;
    const jsonLength = view.getUint32(12, true);
    if (view.getUint32(16, true) !== JSON_CHUNK) return null;
    // Generous ceiling: a JSON chunk past this is not worth reading twice.
    if (jsonLength > 64 * 1024 * 1024) return null;
    return readGlbTextureInfo(await file.slice(0, 20 + jsonLength).arrayBuffer());
  } catch {
    return null;
  }
}
